import { getNodeShiftParams } from "@towersystems/roam-common/lib/shared-utils";
import { useRef, useState } from "react";
import { Animated, Easing } from "react-native";
import { FifoQueueStrategy, Queue } from "../../queue";
import { isDefined } from "../../transforms";
import { Registry } from "../registry";
import { ShiftParams, UseAnimatorReturn } from "../types";
import { computeShiftDeltaValue, Direction, range } from "../utils";
import { promisify } from "../../promisify";
import {
  defaultShiftParams,
  DEFAULT_HOMING_POINT,
  HOMING_ANIMATION_DURATION,
} from "../constants";
import { Point } from "../../drag-drop";

export type UseAnimatorProps = {
  getIdByPosition: (position: number) => string | undefined;
  getPositionById: (id: string) => number | undefined;
  getCoordsByZoneId: (id: string) => Point | undefined;
  getComponentWidth: () => number;
  itemsPerPage: number;
  numColumns: number;
};

/**
 * This hook is responsible for animating the nodes in the layout
 * shifting and restoring animations are queued and executed in order
 * @returns controller methods for the animations and the animation registry
 */
export function useAnimator({
  getIdByPosition,
  getPositionById,
  getCoordsByZoneId,
  getComponentWidth,
  itemsPerPage,
  numColumns,
}: UseAnimatorProps): UseAnimatorReturn {
  const animationRegistry = useRef(new Registry<Animated.ValueXY>()).current;

  const [animationRunning, setAnimationRunning] = useState(false);

  const [animationQueue] = useState(
    new Queue(new FifoQueueStrategy(), setAnimationRunning)
  );

  const shiftParams = useRef<ShiftParams & { target?: string }>(
    defaultShiftParams
  );

  function shift(id: string, position: number, callback?: () => void) {
    const oldPosition = getPositionById(id);

    if (oldPosition === undefined) return;

    shiftParams.current = {
      ...getNodeShiftParams(oldPosition, position),
      target: id,
    };

    const getAnimations = () =>
      range(shiftParams.current.start, shiftParams.current.end + 1)
        .map((nodePosition) => {
          const shiftingInfo = getShiftDeltaArgs(nodePosition);

          if (!shiftingInfo) return;

          const { animatedValue, shiftDeltaArgs, newPosition } = shiftingInfo;

          return Animated.timing(animatedValue, {
            toValue: computeShiftDeltaValue({
              ...shiftDeltaArgs,
              componentWidth: getComponentWidth(),
              numColumns,
            }),
            duration: 400,
            useNativeDriver: true,
          });
        })
        .filter(isDefined);

    requestAnimation(getAnimations, (animations) => {
      animations.forEach((animation) => animation?.reset());
      callback?.();
    });
  }

  function restorePositions(callback?: () => void) {
    const getAnimations = () =>
      range(shiftParams.current.start, shiftParams.current.end + 1)
        .map((nodePosition) => {
          const args = getShiftDeltaArgs(nodePosition);
          if (!args) return;

          const shiftDeltaInfo = computeShiftDeltaValue({
            ...args.shiftDeltaArgs,
            componentWidth: getComponentWidth(),
            numColumns,
          });

          const { animatedValue } = args;
          animatedValue.setValue(shiftDeltaInfo);
          return Animated.timing(animatedValue, {
            toValue: {
              x: 0,
              y: 0,
            },
            duration: 400,
            useNativeDriver: true,
          });
        })
        .filter(isDefined);
    requestAnimation(getAnimations, (animations) => {
      animations.forEach((animation) => animation?.reset());
      shiftParams.current = defaultShiftParams;
      callback?.();
    });
  }

  function homing(id: string, position: number, callback?: () => void) {
    const anim = animationRegistry.get(id);
    const homingTarget =
      getCoordsByZoneId(`${position}`) || DEFAULT_HOMING_POINT;
    const ease = Animated.timing(anim, {
      duration: HOMING_ANIMATION_DURATION,
      easing: Easing.elastic(1),
      useNativeDriver: true,
      toValue: homingTarget,
    });
    anim?.flattenOffset();
    ease.start(() => {
      anim?.setValue({ x: 0, y: 0 });
      callback?.();
      ease.reset();
    });
  }

  function requestAnimation(
    getAnimations: () => Animated.CompositeAnimation[],
    callback: (animations: Animated.CompositeAnimation[]) => void
  ) {
    animationQueue.enqueue(() => {
      const animations = getAnimations();
      return parallelAnimationPromise(animations).then(() => {
        callback(animations);
      });
    });

    animationQueue.processAll();
  }

  const parallelAnimationPromise = promisify<
    Animated.CompositeAnimation[],
    any
  >((args, callback) => Animated.parallel(args).start(callback));

  function getShiftDeltaArgs(nodePosition: number) {
    const nodeId = getIdByPosition(nodePosition);

    if (nodeId === undefined || !animationRegistry.has(nodeId)) {
      return;
    }

    const animatedValue = animationRegistry.get(nodeId);

    const newPosition = nodePosition + (shiftParams.current?.offset || 0);

    let fromValue = getCoordsByZoneId(`${nodePosition}`);

    let toValue = getCoordsByZoneId(`${newPosition}`);

    if (!fromValue && !toValue) {
      return;
    }

    const newPositionPageQuotient = Math.floor(newPosition / itemsPerPage);
    const oldPositionPageQuotient = Math.floor(nodePosition / itemsPerPage);

    const isShiftingToNewPage =
      newPositionPageQuotient !== oldPositionPageQuotient;

    if (isShiftingToNewPage) {
      toValue = undefined;
    }

    const res = {
      animatedValue,
      shiftDeltaArgs: {
        fromValue,
        toValue,
        direction:
          newPosition > nodePosition ? Direction.Backward : Direction.Forward,
      },
      newPosition,
    };

    return res;
  }

  return {
    animationRegistry,
    animationRunning,
    shift,
    homing,
    restorePositions,
  };
}
